import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrophyIcon } from "@phosphor-icons/react";

import { fetchTeam } from "../../lib/reports";
import { downloadCsv, reportFilename } from "../../lib/csv";
import ExportCsvButton from "./ExportCsvButton";

function Skeleton() {
  return <div className="h-48 animate-pulse rounded bg-slate-100" />;
}

function NoData({ label = "No data for this period" }) {
  return (
    <div className="flex h-40 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
      {label}
    </div>
  );
}

function Section({ title, subtitle, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white shadow-card"
    >
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="font-heading text-sm font-semibold text-slate-900">
          {title}
        </div>
        {subtitle && (
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white p-4 shadow-card"
    >
      <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums mt-1.5">
        {value}
      </div>
    </div>
  );
}

export default function TeamTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    fetchTeam(params)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Failed to load team");
        setLoading(false);
      });
  };

  useEffect(load, [params.date_from, params.date_to]);

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="team-error"
        className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        {err}{" "}
        <button onClick={load} className="ml-3 underline" data-testid="team-retry">
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  const exportCsv = () => {
    const rows = [];
    rows.push(["Counts", "Active members", data.active_team_members, ""]);
    rows.push(["Counts", "Avg jobs per tech", data.avg_jobs_per_tech ?? "", ""]);
    rows.push(["Counts", "Top performer", data.top_performer ?? "", ""]);
    rows.push(["Counts", "Audit events in range", data.audit_events_in_range, ""]);
    rows.push(["Counts", "Logins in range", data.logins_in_range, ""]);
    rows.push(["Counts", "Password changes in range", data.password_changes_in_range, ""]);
    (data.jobs_per_tech || []).forEach((t) =>
      rows.push(["JobsPerTech", t.tech_name, t.completed, `in_progress=${t.in_progress}`])
    );
    (data.recent_role_changes || []).forEach((r) =>
      rows.push([
        "RoleChange",
        r.target_name || "",
        `${r.old_role || ""} -> ${r.new_role || ""}`,
        `actor=${r.actor_name || ""};at=${r.changed_at}`,
      ])
    );
    downloadCsv(
      reportFilename("team", params),
      ["Section", "Key", "Value", "Detail"],
      rows
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportCsvButton onExport={exportCsv} testId="export-csv-team" />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Active members"
          value={data.active_team_members}
          testId="team-active"
        />
        <StatCard
          label="Avg jobs / tech"
          value={
            data.avg_jobs_per_tech === null || data.avg_jobs_per_tech === undefined
              ? "—"
              : data.avg_jobs_per_tech.toFixed(1)
          }
          testId="team-avg-jobs"
        />
        <StatCard
          label="Logins"
          value={data.logins_in_range}
          testId="team-logins"
        />
        <StatCard
          label="Password changes"
          value={data.password_changes_in_range}
          testId="team-password-changes"
        />
      </div>

      {data.top_performer && (
        <div
          data-testid="team-top-performer"
          className="flex items-center gap-4 rounded-md border border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100/60 p-4 shadow-card"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-white">
            <TrophyIcon size={20} weight="fill" />
          </div>
          <div>
            <div className="font-data text-[10px] uppercase tracking-[0.18em] text-amber-700">
              Top performer
            </div>
            <div className="font-heading text-lg font-bold text-amber-900">
              {data.top_performer}
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Jobs per tech"
          subtitle="completed in selected period"
          testId="team-jobs-chart"
        >
          {(data.jobs_per_tech || []).length === 0 ? (
            <NoData />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, data.jobs_per_tech.length * 36)}>
              <BarChart
                data={data.jobs_per_tech}
                layout="vertical"
                margin={{ top: 5, right: 20, bottom: 5, left: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="tech_name"
                  tick={{ fontSize: 11 }}
                  width={120}
                />
                <Tooltip />
                <Bar
                  dataKey="completed"
                  fill="#10b981"
                  radius={[0, 4, 4, 0]}
                  name="Completed"
                />
                <Bar
                  dataKey="in_progress"
                  fill="#f59e0b"
                  radius={[0, 4, 4, 0]}
                  name="In progress"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section
          title="Recent role changes"
          subtitle="last 10 in range"
          testId="team-role-changes"
        >
          {(data.recent_role_changes || []).length === 0 ? (
            <NoData label="No role changes in this period" />
          ) : (
            <table className="w-full text-sm">
              <thead className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="py-2 text-left font-bold">Target</th>
                  <th className="py-2 text-left font-bold">Change</th>
                  <th className="py-2 text-left font-bold">By</th>
                  <th className="py-2 text-left font-bold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.recent_role_changes.map((r, i) => (
                  <tr
                    key={i}
                    data-testid={`team-role-change-${i}`}
                    className="text-xs"
                  >
                    <td className="py-2 font-medium text-slate-900">
                      {r.target_name || "—"}
                    </td>
                    <td className="py-2 text-slate-700">
                      <span className="font-data">{r.old_role || "—"}</span>{" "}
                      → <span className="font-data font-bold">{r.new_role || "—"}</span>
                    </td>
                    <td className="py-2 text-slate-600">{r.actor_name || "—"}</td>
                    <td className="py-2 font-data text-slate-500">
                      {new Date(r.changed_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </div>
  );
}
